import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { firstValueFrom } from 'rxjs';
import { RemissionDetailModel } from 'src/app/models/rental/remission-detail';
import { RemissionDetailFromOrderModel } from 'src/app/models/rental/remission-detail-from-order.model';
import { RemissionModel } from 'src/app/models/rental/remission.model';
import { ArticleService } from 'src/app/services/archive/article.service';
import { StoreService } from 'src/app/services/archive/store.service';
import { FormControlStateService } from 'src/app/services/global-services/form-control-state.service';
import { RemissionFromOrderService } from 'src/app/services/rental/remission-from-order.service';
import { ToastHandlerService } from 'src/app/services/toast/toast-handler.service';
import Swal from 'sweetalert2';

@Component({
  selector: 'app-register-manual-order',
  templateUrl: './register-manual-order.component.html',
  styleUrls: ['./register-manual-order.component.scss'],
  standalone: false
})
export class RegisterManualOrderComponent implements OnChanges {

  @Input() remissionContract: RemissionModel = new RemissionModel();
  @Input() remissionDetailFromOrder: RemissionDetailFromOrderModel = new RemissionDetailFromOrderModel();
  @Output() isFormOrderView = new EventEmitter<boolean>();
  @Output() reloadRemissionContract = new EventEmitter<boolean>();
  @Output() updateListDetail = new EventEmitter<boolean>();

  articleId: number = 0;
  codeNameRemissionArticle: string = '';
  codeNameStore: string = '';
  formRemissionOrderDetail: FormGroup = new FormGroup({});
  orderDetailId: number = 0;
  remissionDetailId: number = 0;

  constructor(private formBuilder: FormBuilder,
    private formControlStateService: FormControlStateService,
    private remissionFromOrderService: RemissionFromOrderService,
    private storeService: StoreService,
    private articleService: ArticleService,
    private toastHandlerService: ToastHandlerService) {
    this.validateForm(new RemissionDetailFromOrderModel());
  }

  async ngOnChanges(changes: SimpleChanges) {
    const remissionDetailFromOrder = changes['remissionDetailFromOrder'];
    if (remissionDetailFromOrder) {
      if (this.remissionDetailFromOrder.remissionDetailId == 0) {
        this.createNewRemissionFromOrderDetail();
      } else {
        this.remissionDetailId = this.remissionDetailFromOrder.remissionDetailId;
        this.validateForm(this.remissionDetailFromOrder);
      }
    }
  }

  createNewRemissionFromOrderDetail() {
    this.remissionDetailId = 0;
    this.codeNameStore = '';
    this.codeNameRemissionArticle = '';
    this.validateForm(this.remissionDetailFromOrder);
  }

  async createManualOrder(remissionDetail: RemissionDetailModel) {
    try {
      await firstValueFrom(this.remissionFromOrderService.createManualOrder(remissionDetail));
      this.toastHandlerService.showToast('success', 'El detalle de la remisión fue creado exitosamente.');
      this.updateListDetail.emit(true);
      this.reloadRemissionContract.emit(true);
      this.clearForm();
    } catch (error) {
      console.error('Error al crear el detalle de remisión.', error);
    }
  }

  clearForm() {
    this.articleId = 0;
    this.codeNameStore = '';
    this.codeNameRemissionArticle = '';
    this.isFormOrderView.emit(false);
    this.remissionDetailFromOrder = new RemissionDetailFromOrderModel();
    this.validateForm(this.remissionDetailFromOrder);
  }

  async configureFormControls(remissionDetail: RemissionDetailFromOrderModel) {
    const disabledControls = ['quantityFinal', 'quantityPending', 'stock', 'quantityReserved'];
    this.formControlStateService.setControlState(
      this.formRemissionOrderDetail,
      disabledControls,
      false
    );
    const { storeId, articleId } = remissionDetail;
    if (!storeId || !articleId) return;
    await Promise.all([
      this.getStore(storeId),
      this.getArticle(articleId)
    ]);
  }

  async changeQuantity(newQuantity: number): Promise<void> {
    const controls = this.formRemissionOrderDetail.controls;
    const currentStock = controls["stock"].value;
    const currentReserved = controls["quantityReserved"].value;
    const currentPending = controls["quantityPending"].value;
    const orderQuantity = controls["quantityFinal"].value;
    const previousQuantity = this.remissionDetailFromOrder.quantity;
    const maxAvailableStock = currentStock + previousQuantity;
    let maxAllowedQuantity = newQuantity;
    let warningMessage: string | null = null;
    if (newQuantity > orderQuantity) {
      maxAllowedQuantity = orderQuantity;
      warningMessage = `La cantidad (${newQuantity}) supera la cantidad del pedido (${orderQuantity}). ¿Desea despachar el máximo permitido?`;
    }
    if (newQuantity > maxAvailableStock) {
      maxAllowedQuantity = Math.min(maxAllowedQuantity, maxAvailableStock);
      warningMessage = `La cantidad (${newQuantity}) supera la existencia disponible (${maxAvailableStock}). ¿Desea despachar el máximo permitido?`;
    }
    if (warningMessage) {
      const { isConfirmed } = await Swal.fire({
        title: 'Atención',
        text: warningMessage,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Sí',
        cancelButtonText: 'No',
        confirmButtonColor: '#3085d6',
        cancelButtonColor: '#d33'
      });
      if (!isConfirmed) {
        controls["quantity"].patchValue(previousQuantity);
        return;
      }
      newQuantity = maxAllowedQuantity;
    }
    const quantityDifference = newQuantity - previousQuantity;
    const updatedStock = maxAvailableStock - newQuantity;
    const updatedPending = currentPending - quantityDifference;
    let updatedReserved = currentReserved - quantityDifference;
    if (updatedReserved < 0) updatedReserved = 0;
    controls["stock"].patchValue(updatedStock);
    controls["quantity"].patchValue(newQuantity);
    controls["quantityPending"].patchValue(updatedPending);
    controls["quantityReserved"].patchValue(updatedReserved);
    this.remissionDetailFromOrder.quantity = newQuantity;
  }

  async getArticle(articleId: number) {
    try {
      const article = await firstValueFrom(this.articleService.getArticleById(articleId));
      if (!article.active) {
        this.toastHandlerService.showToast(
          'warning',
          'El artículo seleccionado se encuentra inactivo.',
        );
      }
      this.codeNameRemissionArticle = `${article.code} - ${article.name}`;
    } catch (error) {
      console.error('Error al obtener el articulo por Id.', error);
    }
  }

  async getStore(storeId: number) {
    try {
      const store = await firstValueFrom(this.storeService.getStoreById(storeId));
      this.codeNameStore = `${store.code} - ${store.name}`;
    } catch (error) {
      console.error('Error al obtener la bodega por Id.', error);
    }
  }

  async send() {
    this.validateFormControls();
    if (this.formRemissionOrderDetail.invalid) {
      this.showValidationMessages();
      return;
    }
    const remissionDetail = {
      storeId: this.remissionDetailFromOrder.storeId,
      articleId: this.remissionDetailFromOrder.articleId,
      remissionId: this.remissionDetailFromOrder.remissionId,
      orderDetailId: this.remissionDetailFromOrder.orderDetailId,
      quantity: this.formRemissionOrderDetail.getRawValue().quantity
    } as RemissionDetailModel;
    if (this.remissionDetailId === 0) {
      await this.createManualOrder(remissionDetail);
    } else {
      remissionDetail.remissionDetailId = this.remissionDetailFromOrder.remissionDetailId;
      await this.updateManualOrder(remissionDetail);
    }
  }

  showValidationMessages() {
    const controlMessages: Record<string, Record<string, string>> = {
      articleId: {
        required: 'El campo Artículo es obligatorio.',
        min: 'Debes seleccionar un Artículo válido.',
      },
      storeId: {
        required: 'El campo Bodega es obligatorio.',
        min: 'Debes seleccionar una Bodega válida.',
      },
      quantity: {
        required: 'El campo Cantidad es obligatorio.',
        min: 'La Cantidad no puede ser negativa o igual a cero.',
        max: 'La Cantidad no puede superar 9.999.999,99.',
      },
    };

    Object.keys(controlMessages).forEach(control => {
      const formControl = this.formRemissionOrderDetail.get(control);
      if (formControl && formControl.invalid && formControl.errors) {
        const messages = controlMessages[control];
        Object.keys(formControl.errors).forEach(errorKey => {
          const message = messages[errorKey];
          if (message) {
            this.toastHandlerService.showToast('warning', message, 'Atención');
          }
        });
      }
    });
  }

  async updateManualOrder(remissionDetail: RemissionDetailModel) {
    try {
      await firstValueFrom(this.remissionFromOrderService.updateManualOrder(remissionDetail));
      this.toastHandlerService.showToast('success', 'El detalle de la remisión fue modificado exitosamente.');
      this.updateListDetail.emit(true);
      this.reloadRemissionContract.emit(true);
      this.clearForm();
    } catch (error) {
      console.error('Error al modificar el detalle de remisión.', error);
    }
  }

  async validateForm(remissionDetailFromOrder: RemissionDetailFromOrderModel) {
    this.formRemissionOrderDetail = this.formBuilder.group({
      orderDetailId: [remissionDetailFromOrder.orderDetailId, [Validators.required]],
      articleId: [remissionDetailFromOrder.articleId, [Validators.required, Validators.min(1)]],
      storeId: [remissionDetailFromOrder.storeId, [Validators.required, Validators.min(1)]],
      quantityFinal: [remissionDetailFromOrder.quantityFinal],
      quantityPending: [remissionDetailFromOrder.quantityPending],
      quantityReserved: [remissionDetailFromOrder.quantityReserved],
      stock: [remissionDetailFromOrder.stock],
      quantity: [remissionDetailFromOrder.quantity, [Validators.required, Validators.min(1), Validators.max(9999999.99)]],
    });
    await this.configureFormControls(remissionDetailFromOrder);
  }

  validateFormControls() {
    Object.keys(this.formRemissionOrderDetail.controls).forEach((key) => {
      const control = this.formRemissionOrderDetail.get(key);
      if (control && control.invalid) {
        console.warn(
          `${key} es inválido`,
          control.errors,
          'value:',
          control.value
        );
      }
    });
  }
}
